import { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import type { MatchSportWebViewRef } from '../../../src/quiz/hooks/useMatchSportGame';
import type { MatchSportAudioPayload, MatchSportStartPayload } from '../../../src/quiz/constants/matchSport.constants';

interface Props {
  html: string;
  onMessage: (event: { nativeEvent: { data: string } }) => void;
}

/**
 * Web host: the game runs in a same-origin `srcDoc` iframe. The game posts to `window.parent`; we
 * forward those as `{ nativeEvent: { data } }` so the hook's handler is platform-agnostic. Boot data
 * is applied by calling the iframe's exposed `__matchSportApplyStart` directly (srcDoc inherits
 * the parent origin).
 */
export const MatchSportWebView = forwardRef<MatchSportWebViewRef, Props>(function MatchSportWebView({ html, onMessage }, ref) {
  const frameRef = useRef<HTMLIFrameElement>(null);

  useImperativeHandle(ref, () => ({
    applyStart: (payload: MatchSportStartPayload) => {
      const win = frameRef.current?.contentWindow as (Window & { __matchSportApplyStart?: (p: MatchSportStartPayload) => void }) | null;
      win?.__matchSportApplyStart?.(payload);
    },
    applyAudio: (payload: MatchSportAudioPayload) => {
      const win = frameRef.current?.contentWindow as (Window & { __matchSportApplyAudio?: (p: MatchSportAudioPayload) => void }) | null;
      win?.__matchSportApplyAudio?.(payload);
    },
    reload: () => {
      // Re-running srcDoc reboots the runtime (re-posts `ready`).
      const el = frameRef.current;
      if (el) { const doc = el.srcdoc; el.srcdoc = ''; el.srcdoc = doc; }
    },
  }), []);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.source !== frameRef.current?.contentWindow) return;
      if (typeof e.data !== 'string') return;
      onMessage({ nativeEvent: { data: e.data } });
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onMessage]);

  return (
    <iframe
      ref={frameRef}
      title="Match Sport"
      srcDoc={html}
      style={{ flex: 1, width: '100%', height: '100%', border: 'none', background: 'transparent' }}
    />
  );
});
